import styled from 'foundations-theming-specialty/utils';
import { HR } from 'foundations-components/transitional/components/HR';
import { Button } from 'foundations-components/transitional/components/Button';
import { useOnDelayedHover } from '../utils/useOnDelayedHover';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Box } from 'foundations-components/transitional/components/Box';
import { MOBILE_BREAKPOINT } from '../constants';
import { useMemo } from 'react';
import { usePortalData } from '../data/usePortalData';
import { renderLoadingCell, renderPropertyValue } from '../utils/formatCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Grid = styled.div.withConfig({
  displayName: "TicketListingsGrid__Grid"
})(["display:grid;grid-template-columns:repeat(1,1fr);word-wrap:break-word;@media (min-width:", "px){grid-template-columns:repeat(2,1fr);}@media (min-width:1200px){grid-template-columns:repeat(3,1fr);}@media (min-width:1600px){grid-template-columns:repeat(4,1fr);}@media (min-width:2100px){grid-template-columns:repeat(5,1fr);}gap:", ";color:", ";"], MOBILE_BREAKPOINT, ({
  theme
}) => theme.space[600], ({
  theme
}) => theme.color['text-core-default']);
const DL = styled.dl.withConfig({
  displayName: "TicketListingsGrid__DL"
})(["margin:0;display:grid;grid-template-columns:repeat(2,1fr);grid-template-rows:auto auto;grid-auto-rows:auto;gap:", ";hr{grid-column:1 / -1;}"], ({
  theme
}) => theme.space[400]);
const CardContents = styled.div.withConfig({
  displayName: "TicketListingsGrid__CardContents"
})(["dt{", ";margin-block-end:", ";}dd{", ";margin:0;}&[data-column-id='status']{grid-row:1 / 2;grid-column:1 / 3;}&[data-column-id='subject']{grid-row:2 / 3;grid-column:1 / 3;overflow:hidden;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;}&[data-column-id='__requested_by_full_name_or_email']{grid-row:3 / 4;grid-column:1 / 3;}&[data-column-id='subject'] dt,&[data-column-id='status'] dt,&[data-column-id='__requested_by_full_name_or_email'] dt{display:none;}&[data-column-id='subject'] dd{", ";}"], ({
  theme
}) => theme.typography['detail-100'], ({
  theme
}) => theme.space[100], ({
  theme
}) => theme.typography['heading-25'], ({
  theme
}) => theme.typography['heading-400']);
const VISIBLE_TICKET_PROPERTIES = ['createdate', 'last_ticket_activity'];
const SPECIAL_TICKET_PROPERTIES = ['status', '__hubspot_owner_full_name', '__requested_by_full_name_or_email', 'hs_pipeline_stage', 'ticket_stage', 'subject'];
const CardCell = ({
  property,
  row,
  isLoading
}) => {
  if (!property) return null;
  return /*#__PURE__*/_jsxs(CardContents, {
    "data-column-id": property.propertyName,
    children: [/*#__PURE__*/_jsx("dt", {
      children: property.displayNameOverride
    }), /*#__PURE__*/_jsx("dd", {
      children: isLoading ? renderLoadingCell(property) : renderPropertyValue(row[property.propertyName], property, row, true)
    })]
  });
};
export const TicketListingsGrid = ({
  data,
  isLoading,
  onRowClick,
  onRowHover,
  isUsingPillBorderRadius
}) => {
  var _portalData$data3;
  const portalData = usePortalData();
  const configuredTicketProperties = useMemo(() => {
    var _portalData$data, _portalData$data2;
    if (!((_portalData$data = portalData.data) !== null && _portalData$data !== void 0 && _portalData$data.configuredTicketProperties)) return [];
    return (_portalData$data2 = portalData.data) === null || _portalData$data2 === void 0 ? void 0 : _portalData$data2.configuredTicketProperties;
  }, [(_portalData$data3 = portalData.data) === null || _portalData$data3 === void 0 ? void 0 : _portalData$data3.configuredTicketProperties]);
  const configuredTicketPropertyByPropertyName = useMemo(() => {
    return configuredTicketProperties.reduce((acc, property) => {
      acc[property.propertyName] = property;
      return acc;
    }, {});
  }, [configuredTicketProperties]);
  const tableRowProps = useOnDelayedHover(id => onRowHover(Number(id)));
  const visibleTicketProperties = useMemo(() => {
    const properties = configuredTicketProperties.filter(property => VISIBLE_TICKET_PROPERTIES.includes(property.propertyName));
    const remainingProperties = configuredTicketProperties.filter(property => !VISIBLE_TICKET_PROPERTIES.includes(property.propertyName) && !SPECIAL_TICKET_PROPERTIES.includes(property.propertyName));
    return [...properties, ...remainingProperties.slice(0, VISIBLE_TICKET_PROPERTIES.length - properties.length)];
  }, [configuredTicketProperties]);
  if (!isLoading && data.length === 0) {
    return /*#__PURE__*/_jsxs(Flex, {
      padding: "400",
      flexDirection: "column",
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "heading-200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.noResults"
        })
      }), /*#__PURE__*/_jsx(Text, {
        typography: "body-100",
        color: "text-core-subtle",
        marginBlockStart: "100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.noResultsHelp"
        })
      })]
    });
  }
  return /*#__PURE__*/_jsx(Grid, {
    children: data.map(row => /*#__PURE__*/_jsx(Flex, Object.assign({
      flexDirection: "column",
      gap: "600",
      borderRadius: isUsingPillBorderRadius ? '0' : '400',
      backgroundColor: "fill-surface-raised",
      padding: "600",
      boxShadow: "100",
      id: row.hs_ticket_id.toString()
    }, tableRowProps, {
      children: /*#__PURE__*/_jsxs(DL, {
        children: [/*#__PURE__*/_jsx(CardCell, {
          property: configuredTicketPropertyByPropertyName['status'],
          row: row,
          isLoading: isLoading
        }), /*#__PURE__*/_jsx(CardCell, {
          property: configuredTicketPropertyByPropertyName['subject'],
          row: row,
          isLoading: isLoading
        }), /*#__PURE__*/_jsx(CardCell, {
          property: configuredTicketPropertyByPropertyName['__requested_by_full_name_or_email'],
          row: row,
          isLoading: isLoading
        }), visibleTicketProperties.map(property => /*#__PURE__*/_jsxs(CardContents, {
          "data-column-id": property.propertyName,
          children: [/*#__PURE__*/_jsx("dt", {
            children: property.displayNameOverride
          }), /*#__PURE__*/_jsx("dd", {
            children: isLoading ? renderLoadingCell(property) : renderPropertyValue(row[property.propertyName], property, row, true)
          })]
        }, property.propertyName)), /*#__PURE__*/_jsx(HR, {
          marginBlock: "0"
        }), /*#__PURE__*/_jsx(Box, {
          children: /*#__PURE__*/_jsx(CardCell, {
            property: configuredTicketPropertyByPropertyName['__hubspot_owner_full_name'],
            row: row,
            isLoading: isLoading
          })
        }), /*#__PURE__*/_jsx(Flex, {
          justifyContent: "end",
          children: /*#__PURE__*/_jsx(Button, {
            use: "secondary",
            onClick: () => onRowClick(row.hs_ticket_id),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customer-portal-app.viewTicket"
            })
          })
        })]
      })
    }), row.hs_ticket_id.toString()))
  });
};